import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsBelow } from '@/hooks/useBreakpoint';

export interface MultiSelectOption {
  id: number;
  label: string;
  group?: string;
  color?: string;
}

export interface MultiSelectDropdownProps {
  options: MultiSelectOption[];
  selected: Set<number>;
  onToggle: (id: number) => void;
  /**
   * Called with the ids of the currently-visible options when a search term is
   * narrowing the list, and with `undefined` when it is not. Handlers should
   * MERGE the scoped ids into the existing selection rather than replace it —
   * "Select all" while searching must not drop picks the search is hiding.
   */
  onSelectAll: (visibleIds?: number[]) => void;
  onClearAll: () => void;
  placeholder: string;
  searchable?: boolean;
  className?: string;
}

export function MultiSelectDropdown({
  options,
  selected,
  onToggle,
  onSelectAll,
  onClearAll,
  placeholder,
  searchable = false,
  className,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  /** Touch tier. Drives outside-dismissal and whether a search field autofocuses. */
  const isTouchTier = useIsBelow('md');
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    }
    // Below `md`, `pointerdown`: iOS Safari dispatches no mouse event at all for a tap
    // on a non-interactive element, which leaves this panel undismissable on a phone.
    // Desktop deliberately stays on `mousedown`. Same treatment, and same reasoning, as
    // `Select.tsx` — these are its siblings and carried the identical defect.
    const outsideEvent = isTouchTier ? 'pointerdown' : 'mousedown';
    document.addEventListener(outsideEvent, onOutside);
    return () => document.removeEventListener(outsideEvent, onOutside);
  }, [open, isTouchTier]);

  // Focus search when dropdown opens
  useEffect(() => {
    // No deferred autofocus on a touch screen. It produces a focus ring with no
    // keyboard — iOS raises one only for a real user gesture — and then swallows the
    // tap that would have raised it, because the field is already focused. Let the
    // tap be the focus event. Same conclusion `Select.tsx` and `sheet.tsx` reached.
    if (isTouchTier) return;
    if (open && searchable) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
  }, [open, searchable, isTouchTier]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setSearch('');
      }
    },
    [],
  );

  const isSearching = search.trim().length > 0;

  // Filter options by search term
  const filteredOptions = useMemo(() => {
    if (!search.trim()) return options;
    const term = search.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(term) ||
        (o.group && o.group.toLowerCase().includes(term)),
    );
  }, [options, search]);

  // Group filtered options by their group property
  const groupedOptions = useMemo(() => {
    const hasGroups = filteredOptions.some((o) => o.group);
    if (!hasGroups) return null;

    const groups = new Map<string, MultiSelectOption[]>();
    for (const opt of filteredOptions) {
      const groupName = opt.group ?? 'Other';
      if (!groups.has(groupName)) groups.set(groupName, []);
      groups.get(groupName)!.push(opt);
    }
    return groups;
  }, [filteredOptions]);

  // Button label
  const buttonLabel =
    selected.size === 0
      ? placeholder
      : selected.size === options.length
        ? `All ${placeholder}`
        : `${selected.size} selected`;

  return (
    <div className={cn('relative', className)} ref={containerRef}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={cn(
          'w-full flex items-center gap-2 text-left',
          'px-2.5 py-1.5 text-xs rounded border border-slate-200 bg-white',
          'hover:border-slate-300 transition-colors cursor-pointer',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
          selected.size > 0 && 'border-blue-300 bg-blue-50/50',
        )}
      >
        <span className={cn('flex-1 truncate', selected.size === 0 && 'text-slate-400')}>
          {buttonLabel}
        </span>
        <ChevronDown
          className={cn(
            'h-3.5 w-3.5 text-slate-400 transition-transform duration-150 flex-shrink-0',
            open && 'rotate-180',
          )}
        />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className={cn(
            'absolute z-50 mt-1 w-full min-w-[14rem] rounded-md bg-white border border-slate-200 shadow-lg',
            'animate-scale-in origin-top',
          )}
          onKeyDown={handleKeyDown}
        >
          {/* Search input */}
          {searchable && (
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* Scrollable options list */}
          <div className="max-h-60 overflow-y-auto py-1">
            {filteredOptions.length === 0 ? (
              <p className="px-3 py-2 text-xs text-slate-400 text-center">No options found</p>
            ) : groupedOptions ? (
              // Grouped rendering
              Array.from(groupedOptions.entries()).map(([groupName, groupOpts]) => (
                <div key={groupName}>
                  <p className="px-3 pt-2 pb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wide sticky top-0 bg-white">
                    {groupName}
                  </p>
                  {groupOpts.map((opt) => (
                    <OptionRow
                      key={opt.id}
                      option={opt}
                      checked={selected.has(opt.id)}
                      onToggle={onToggle}
                    />
                  ))}
                </div>
              ))
            ) : (
              // Flat rendering
              filteredOptions.map((opt) => (
                <OptionRow
                  key={opt.id}
                  option={opt}
                  checked={selected.has(opt.id)}
                  onToggle={onToggle}
                />
              ))
            )}
          </div>

          {/* Select All / Clear footer */}
          <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() =>
                onSelectAll(isSearching ? filteredOptions.map((o) => o.id) : undefined)
              }
              disabled={isSearching && filteredOptions.length === 0}
              title={
                isSearching
                  ? `Add the ${filteredOptions.length} matching option${filteredOptions.length === 1 ? '' : 's'} to the selection`
                  : 'Select every option'
              }
              className="text-[11px] font-medium text-blue-600 hover:text-blue-700 transition-colors disabled:cursor-not-allowed disabled:text-slate-300"
            >
              {isSearching ? `Select ${filteredOptions.length}` : 'Select All'}
            </button>
            <button
              type="button"
              onClick={onClearAll}
              className="text-[11px] font-medium text-slate-500 hover:text-slate-700 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Single option row with checkbox, optional color dot, and label. */
function OptionRow({
  option,
  checked,
  onToggle,
}: {
  option: MultiSelectOption;
  checked: boolean;
  onToggle: (id: number) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onToggle(option.id)}
      className={cn(
        'w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left',
        'hover:bg-slate-50 transition-colors',
        checked && 'bg-blue-50/50',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        readOnly
        tabIndex={-1}
        className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 cursor-pointer flex-shrink-0 pointer-events-none"
      />
      {option.color && (
        <span
          className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: option.color }}
        />
      )}
      <span className="truncate text-slate-700">{option.label}</span>
    </button>
  );
}
